import { useEffect, useMemo, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'

import {
  useArchiveIncidentWorkflowMutation,
  useIncidentWorkflowsQuery,
} from '@/graphql/generated/hooks'

import { useColumns } from '../components/Columns'
import {
  SORTABLE_COLUMNS,
  WorkflowsSortColumn,
  formatWorkflowRows,
} from '../utils/query'

export const useWorkflowsTable = () => {
  const colorScheme = useLightMode()
  const { showSuccess, showError } = useToasts()
  const [archiveIncidentWorkflow] = useArchiveIncidentWorkflowMutation({
    refetchQueries: ['IncidentWorkflows'],
  })

  const handleArchive = async (versionId: string) => {
    try {
      await archiveIncidentWorkflow({
        variables: { input: { id: versionId } },
      })
      showSuccess('Workflow archived.')
    } catch {
      showError()
    }
  }

  const [filter, setFilter] = useState('')
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [sortColumn, setSortColumn] = useState<WorkflowsSortColumn | null>(
    'createdAt'
  )
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('desc')

  const { data, loading: isFetching } = useIncidentWorkflowsQuery({
    fetchPolicy: 'cache-and-network',
    variables: { filter: { includeArchived: true } },
  })

  const isLoading = isFetching && !data

  const columns = useColumns({
    isCreatorLoading: isLoading,
    onArchive: handleArchive,
  })

  const allRows = useMemo(
    () => formatWorkflowRows(data, filter, sortColumn, sortOrder),
    [data, filter, sortColumn, sortOrder]
  )

  const total = allRows.length
  const pageCount = Math.ceil(total / pageSize)

  // Reset to first page whenever the filter changes.
  useEffect(() => {
    setPageIndex(0)
  }, [filter])

  // Clamp so the slice never reads past the end of allRows (e.g. after a
  // cache-and-network refetch shrinks the dataset without a filter change).
  const safePageIndex = pageCount === 0 ? 0 : Math.min(pageIndex, pageCount - 1)
  const tableData = allRows.slice(
    safePageIndex * pageSize,
    (safePageIndex + 1) * pageSize
  )

  const handleSortChange = (
    column: string | null,
    order: 'asc' | 'desc' | null
  ) => {
    setSortColumn(
      SORTABLE_COLUMNS.includes(column as WorkflowsSortColumn)
        ? (column as WorkflowsSortColumn)
        : null
    )
    setSortOrder(order)
    setPageIndex(0)
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setPageIndex(0)
  }

  return {
    colorScheme,
    columns,
    filter,
    isLoading,
    setFilter,
    tableData,
    total,
    pageIndex,
    setPageIndex,
    pageSize,
    pageCount,
    sortableColumns: SORTABLE_COLUMNS,
    handleSortChange,
    handlePageSizeChange,
  }
}
